import React from 'react';
import { BiCheckCircle } from 'react-icons/bi';
import { AiOutlineClose } from 'react-icons/ai';
import { formatCurrency } from '../utils/CurrencyUtils';
import { formatDate } from '../utils/DateUtils';
import ModalPortal from './lib/modalPortal';

const InfoCard = ({ label, value, colorClass = "text-gray-800" }) => (
    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm flex flex-col justify-between h-full">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</span>
        <span className={`font-semibold text-sm ${colorClass}`}>{value}</span>
    </div>
);

export default function RegistryInfo({
    registry,
    showRegistryInfoModal,
    handleCancelClick,
}) {
    if (!showRegistryInfoModal || !registry) return null;

    const sortByTipo = (a, b) => {
        const order = { pedido: 1, recarga: 2, correcao: 3, pedidosCancelados: 4 };
        return order[a.tipo] - order[b.tipo];
    };

    /** Caminho estruturado (fechamentos a partir da migração V16): sem parsing de texto. */
    const parseFromStructuredData = (jsonText) => {
        const data = JSON.parse(jsonText);
        const itens = (data.itens || []).map(item => ({
            tipo: item.tipo,
            tipoPagamento: item.tipoPagamento,
            contagem: item.contagem,
            valorParcial: Number(item.valor) || 0,
        }));
        if (data.pedidosCancelados) {
            itens.push({
                tipo: 'pedidosCancelados',
                count: data.pedidosCancelados.count || 0,
                total: Number(data.pedidosCancelados.total) || 0,
            });
        }
        // Pagamentos online (PIX/cartão/recarga via foodz-payments): informativo apenas, nunca
        // somado ao caixa físico — não entra em parsedData/totalPedidos/totalRecargas.
        const onlineData = (data.itensOnline || []).map(item => ({
            tipo: item.tipo,
            tipoPagamento: item.tipoPagamento,
            contagem: item.contagem,
            valorParcial: Number(item.valor) || 0,
        }));
        // Crédito Foodz: dinheiro que já entrou no caixa quando foi recarregado — informativo
        // apenas, também fora de parsedData/totalPedidos.
        const creditoFoodzData = (data.itensCreditoFoodz || []).map(item => ({
            tipo: item.tipo,
            tipoPagamento: item.tipoPagamento,
            contagem: item.contagem,
            valorParcial: Number(item.valor) || 0,
        }));
        return {
            parsedData: itens.sort(sortByTipo),
            entradasValue: Number(data.entradas) || 0,
            saidasValue: Number(data.saidas) || 0,
            onlineData,
            creditoFoodzData,
        };
    };

    /** Fallback para fechamentos antigos (anteriores à migração V16), que só têm o texto livre. */
    const parseFromLegacyReportText = (report) => {
        const lines = report.trim().length ? report.trim().split('\n') : [];

        const parsedData = lines
            .map(entry => {
                if (entry.startsWith('Tipo de Pagamento')) {
                    const parts = entry.split(',').map(p => p.trim());
                    const tipoPagamento = parts[0].split(': ')[1];
                    const contagem = parts[1]?.split(': ')[1] ?? '';
                    const valorParcial = Number((parts[2]?.split(': ')[1] ?? '').replace(/[^\d.-]/g, '')) || 0;
                    const status = parts[3]?.split(': ')[1] ?? '';

                    let tipo = 'recarga';
                    if (tipoPagamento.startsWith('Pedido')) tipo = 'pedido';
                    else if (tipoPagamento.startsWith('Correção')) tipo = 'correcao';

                    return { tipo, tipoPagamento, contagem, valorParcial, status };
                } else if (entry.startsWith('Pedidos Cancelados')) {
                    const match = entry.match(/Pedidos Cancelados - (\d+)\s+Valor Total: R\$(.+)/);
                    if (match) {
                        return { tipo: 'pedidosCancelados', count: parseInt(match[1], 10), total: Number(match[2].trim()) || 0 };
                    }
                }
                return null;
            })
            .filter(Boolean)
            .sort(sortByTipo);

        const entradasMatch = report.match(/Entradas - Valor: R\$(\d+(\.\d{2})?)/);
        const saidasMatch = report.match(/Saídas - Valor: R\$(\d+(\.\d{2})?)/);

        return {
            parsedData,
            entradasValue: entradasMatch ? parseFloat(entradasMatch[1]) : 0.0,
            saidasValue: saidasMatch ? parseFloat(saidasMatch[1]) : 0.0,
        };
    };

    let parsedData = [];
    let entradasValue = 0.0;
    let saidasValue = 0.0;
    let onlineData = [];
    let creditoFoodzData = [];

    try {
        if (registry.closing_report_data) {
            ({ parsedData, entradasValue, saidasValue, onlineData = [], creditoFoodzData = [] } = parseFromStructuredData(registry.closing_report_data));
        } else {
            ({ parsedData, entradasValue, saidasValue } = parseFromLegacyReportText(registry.closing_report ?? ''));
        }
    } catch (e) {
        console.error('Falha ao interpretar o relatório de fechamento, usando texto legado.', e);
        ({ parsedData, entradasValue, saidasValue } = parseFromLegacyReportText(registry.closing_report ?? ''));
    }

    const totalPedidos = parsedData
        .filter(item => item.tipo === 'pedido')
        .reduce((sum, item) => sum + item.valorParcial, 0);

    const totalRecargas = parsedData
        .filter(item => item.tipo === 'recarga')
        .reduce((sum, item) => sum + item.valorParcial, 0);

    const status = registry.registry_status === 'closed' ? 'Fechado' : 'Aberto';
    const statusColor = registry.registry_status === 'closed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700';

    return (
        <ModalPortal>
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in duration-200 overflow-hidden">
                    
                    <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Detalhes de Caixa</h2>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full mt-1 inline-block ${statusColor}`}>
                                Status: {status}
                            </span>
                        </div>
                        <button onClick={handleCancelClick} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-slate-200 transition-colors">
                            <AiOutlineClose size={20} />
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6">
                            <InfoCard label="Abertura" value={formatDate(registry.opening_time)} />
                            <InfoCard label="Fechamento" value={formatDate(registry.closing_time)} />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <InfoCard label="Aberto por" value={registry.opened_by} />
                            <InfoCard label="Fechado por" value={registry.closed_by} />
                            
                            <InfoCard label="Valor Inicial" value={formatCurrency(registry.start_value)} />
                            <InfoCard label="Valor Final" value={formatCurrency(registry.final_value)} colorClass="text-custom_blue font-black" />
                            
                            <InfoCard label="Entradas (+)" value={formatCurrency(entradasValue)} colorClass="text-green-600" />
                            <InfoCard label="Saídas (-)" value={formatCurrency(saidasValue)} colorClass="text-red-600" />
                            
                            {totalPedidos > 0 && <InfoCard label="Total Vendas" value={formatCurrency(totalPedidos)} />}
                            {totalRecargas > 0 && <InfoCard label="Total Recargas" value={formatCurrency(totalRecargas)} />}
                        </div>

                        <div>
                            <h3 className="font-bold text-gray-700 mb-3 border-b border-slate-200 pb-1">Relatório de Fechamento</h3>
                            <div className="space-y-3">
                                {parsedData.map((item, idx) => {
                                    if (item.tipo === 'pedido') {
                                        return (
                                            <div key={idx} className="flex justify-between items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                                                <div>
                                                    <p className="font-semibold text-green-900 text-sm">Venda</p>
                                                    <p className="text-xs text-green-700">{item.tipoPagamento.replace('Pedido - ', '')}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-green-700 text-sm">{formatCurrency(item.valorParcial)}</p>
                                                    <p className="text-xs text-green-600">Qtd: {item.contagem}</p>
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (item.tipo === 'recarga') {
                                        return (
                                            <div key={idx} className="flex justify-between items-center p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                                <div>
                                                    <p className="font-semibold text-blue-900 text-sm">Recarga</p>
                                                    <p className="text-xs text-blue-700">{item.tipoPagamento.replace('Recarga - ', '')}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-blue-700 text-sm">{formatCurrency(item.valorParcial)}</p>
                                                    <p className="text-xs text-blue-600">Qtd: {item.contagem}</p>
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (item.tipo === 'correcao') {
                                        return (
                                            <div key={idx} className="flex justify-between items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                                                <div>
                                                    <p className="font-semibold text-red-900 text-sm">Correção</p>
                                                    <p className="text-xs text-red-700">{item.tipoPagamento.replace('Correção - ', '')}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-red-700 text-sm">{formatCurrency(item.valorParcial)}</p>
                                                    <p className="text-xs text-red-600">Qtd: {item.contagem}</p>
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (item.tipo === 'pedidosCancelados') {
                                        return (
                                            <div key={idx} className="flex justify-between items-center p-3 bg-red-100 border border-red-200 rounded-lg opacity-80">
                                                <div>
                                                    <p className="font-semibold text-red-900 text-sm">Cancelamentos</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-red-800 text-sm">{formatCurrency(item.total)}</p>
                                                    <p className="text-xs text-red-700">Qtd: {item.count}</p>
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (item.tipo === 'creditoFoodz') {
                                        return (
                                            <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                                <div>
                                                    <p className="font-semibold text-gray-600 text-sm">Crédito Foodz</p>
                                                    <p className="text-xs text-gray-400">Informativo — não entra no caixa</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-gray-500 text-sm">{formatCurrency(item.total)}</p>
                                                    <p className="text-xs text-gray-400">Qtd: {item.count}</p>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>

                        {onlineData.length > 0 && (
                            <div>
                                <h3 className="font-bold text-gray-700 mb-1 border-b border-slate-200 pb-1">Pagamentos Online (informativo)</h3>
                                <p className="text-xs text-gray-400 mb-3">Esse valor já estava na carteira digital, não no caixa físico — não soma ao Valor Final.</p>
                                <div className="space-y-3">
                                    {onlineData.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                            <div>
                                                <p className="font-semibold text-purple-900 text-sm">{item.tipo === 'pedido' ? 'Venda Online' : 'Recarga Online'}</p>
                                                <p className="text-xs text-purple-700">{item.tipoPagamento.replace('Pedido Online - ', '').replace('Recarga Online - ', '')}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-purple-700 text-sm">{formatCurrency(item.valorParcial)}</p>
                                                <p className="text-xs text-purple-600">Qtd: {item.contagem}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {creditoFoodzData.length > 0 && (
                            <div>
                                <h3 className="font-bold text-gray-700 mb-1 border-b border-slate-200 pb-1">Vendas com Crédito Foodz (informativo)</h3>
                                <p className="text-xs text-gray-400 mb-3">Esse valor já entrou no caixa quando o crédito foi recarregado — não soma ao Valor Final.</p>
                                <div className="space-y-3">
                                    {creditoFoodzData.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <div>
                                                <p className="font-semibold text-amber-900 text-sm">Venda Crédito Foodz</p>
                                                <p className="text-xs text-amber-700">{item.tipoPagamento.replace('Pedido Crédito Foodz - ', '')}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-amber-700 text-sm">{formatCurrency(item.valorParcial)}</p>
                                                <p className="text-xs text-amber-600">Qtd: {item.contagem}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                        <button
                            className="flex items-center gap-2 bg-custom_green hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-md transition-all active:scale-95"
                            onClick={handleCancelClick}
                        >
                            <BiCheckCircle className="text-xl" /> OK
                        </button>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
}